import React from "react";
import { Link } from "react-router-dom";
import { Calculator, Wrench, ClipboardList, PiggyBank, Users, FileText } from "lucide-react";
import { PageLayout, Section, Text, Bullets } from "./PageLayout";

const pillars = [
  {
    icon: Calculator,
    title: "Kaufmännische Verwaltung",
    items: [
      "Hausgeldverwaltung und Buchführung",
      "Zahlungsverkehr und Mahnwesen",
      "Jahresabrechnung und Wirtschaftsplan",
      "Rücklagenverwaltung und Vermögensbericht",
    ],
  },
  {
    icon: Wrench,
    title: "Technische Verwaltung",
    items: [
      "Regelmäßige Objektbegehungen",
      "Instandhaltungsplanung und Mängelerfassung",
      "Einholung und fachkundige Prüfung von Handwerkerangeboten",
      "Koordination und Abnahme von Reparaturen",
    ],
  },
  {
    icon: ClipboardList,
    title: "Organisatorische Verwaltung",
    items: [
      "Vorbereitung und Durchführung der Eigentümerversammlung",
      "Protokolle und Beschlusssammlung",
      "Umsetzung der Beschlüsse",
      "Kommunikation mit Eigentümern, Behörden und Versorgern",
    ],
  },
];

export default function WegVerwaltungKleve() {
  return (
    <PageLayout
      path="/weg-verwaltung-kleve"
      eyebrow="Leistungen"
      title="WEG-Verwaltung in Kleve und am Niederrhein"
      intro="Wir verwalten Eigentümergemeinschaften nach den Vorgaben des Wohnungseigentumsgesetzes: kaufmännisch sauber, technisch kompetent und organisatorisch zuverlässig. Unser Schwerpunkt liegt auf kleinen und mittelgroßen Gemeinschaften, die einen festen Ansprechpartner und kurze Wege schätzen."
      testId="weg-verwaltung-page"
    >
      <div className="grid md:grid-cols-3 gap-5">
        {pillars.map((p) => (
          <div key={p.title} className="rounded-3xl border border-brand-100 bg-white p-6 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 rounded-2xl bg-brand-900 text-white flex items-center justify-center">
              <p.icon className="w-5 h-5" />
            </div>
            <h2 className="mt-4 font-display text-xl font-bold text-brand-900">{p.title}</h2>
            <ul className="mt-3 space-y-2">
              {p.items.map((it) => (
                <li key={it} className="flex items-start gap-2 text-[14px] text-brand-700">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-accent2 shrink-0" />
                  {it}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <Section title="Jahresabrechnung und Wirtschaftsplan">
        <Text>
          Die Jahresabrechnung ist das Herzstück einer transparenten Verwaltung. Wir erstellen sie
          nachvollziehbar, mit klaren Belegen und fristgerecht. Unser Versprechen: die
          Hausgeldabrechnung liegt innerhalb von 10 Werktagen nach Eingang der Heizkostenabrechnung
          vor. Der Wirtschaftsplan wird realistisch kalkuliert, damit die Gemeinschaft nicht von
          Sonderumlagen überrascht wird.
        </Text>
        <Bullets items={["Einzel- und Gesamtabrechnung je Einheit", "Vermögensbericht nach § 28 Abs. 4 WEG", "Realistische Planung der Erhaltungsrücklage", "Belegeinsicht für alle Eigentümer"]} />
      </Section>

      <Section title="Eigentümerversammlungen">
        <Text>
          Wir laden fristgerecht ein, bereiten die Tagesordnung mit Beschlussvorschlägen vor und
          moderieren die Versammlung sachlich. Das Protokoll erhalten alle Eigentümer zeitnah, die
          Beschlusssammlung wird laufend gepflegt. Gefasste Beschlüsse setzen wir konsequent um.
        </Text>
      </Section>

      <Section title="Instandhaltung und Handwerkerkoordination">
        <Text>
          Durch unsere Herkunft aus der Haustechnik erkennen wir Instandhaltungsbedarf früh und
          können Angebote fachlich beurteilen. Wir holen Vergleichsangebote ein, beauftragen
          zuverlässige Betriebe aus unserem Netzwerk, überwachen die Ausführung und prüfen die
          Rechnungen. So bleibt die Anlage in gutem Zustand und die Kosten im Rahmen.
        </Text>
      </Section>

      <Section title="Rücklagenverwaltung">
        <Text>
          Die Erhaltungsrücklage wird getrennt vom laufenden Konto geführt und sicher angelegt. Über
          Stand und Entwicklung informieren wir im Vermögensbericht und in der Versammlung. Größere
          Maßnahmen planen wir mit Vorlauf, damit die Gemeinschaft handlungsfähig bleibt.
        </Text>
      </Section>

      <Section title="Für wen wir arbeiten">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="rounded-2xl bg-brand-50/60 border border-brand-100 p-5 flex gap-4">
            <Users className="w-6 h-6 text-accent2 shrink-0" />
            <div>
              <div className="font-semibold text-brand-900">Kleine und mittelgroße WEGs</div>
              <p className="text-[14px] text-brand-700 mt-1">Gemeinschaften, denen große Verwaltungen oft zu wenig Aufmerksamkeit schenken.</p>
            </div>
          </div>
          <div className="rounded-2xl bg-brand-50/60 border border-brand-100 p-5 flex gap-4">
            <FileText className="w-6 h-6 text-accent2 shrink-0" />
            <div>
              <div className="font-semibold text-brand-900">Fester Ansprechpartner</div>
              <p className="text-[14px] text-brand-700 mt-1">Sie sprechen immer mit derselben Person, die Ihr Objekt kennt.</p>
            </div>
          </div>
        </div>
        <Text>
          <PiggyBank className="inline w-4 h-4 mr-1 text-brand-500" />
          Sie möchten die Verwaltung wechseln? Lesen Sie, wie einfach ein{" "}
          <Link to="/verwalterwechsel" className="underline text-brand-900" data-testid="link-verwalterwechsel">
            Verwalterwechsel
          </Link>{" "}
          abläuft.
        </Text>
      </Section>
    </PageLayout>
  );
}
